import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export function Card({
  title,
  description,
  to,
  children,
}: {
  title?: string;
  description?: string;
  /** When set, renders a "View" link in the header pointing to this route. */
  to?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 p-5 shadow-soft transition hover:shadow-elevated">
      {(title || to) && (
        <div className="flex items-start justify-between gap-3">
          <div>
            {title && (
              <h2 className="font-display text-[15px] font-semibold tracking-tight text-ink-900 dark:text-ink-50">
                {title}
              </h2>
            )}
            {description && <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">{description}</p>}
          </div>
          {to && (
            <Link
              to={to}
              className="group inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/5"
            >
              View
              <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>
      )}
      {!title && !to && description && (
        <p className="text-xs text-ink-500 dark:text-ink-400">{description}</p>
      )}
      <div className={title || description || to ? 'mt-4' : ''}>{children}</div>
    </section>
  );
}

export function StatusBadge({ ok, label }: { ok: boolean; label: string }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-mono text-[10px] font-semibold tracking-[0.08em] ring-1 ring-inset ${
        ok
          ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 ring-brand-200'
          : 'bg-accent-amber/10 text-accent-amber ring-accent-amber/30'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-brand-500' : 'bg-accent-amber'}`} />
      {label.toUpperCase()}
    </span>
  );
}
